import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, AlertCircle, CheckCircle2, Smartphone, Wifi } from "lucide-react";

const ConfirmRedeemModal = ({ open, onClose, onConfirm, redeemData }) => {
  if (!redeemData) return null;

  const getIcon = () => {
    return redeemData.conversionType === "data" ? (
      <Wifi className="w-12 h-12 text-emerald-500" />
    ) : (
      <Smartphone className="w-12 h-12 text-emerald-500" />
    );
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="flex fixed inset-0 z-50 justify-center items-center p-4"
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 backdrop-blur-sm bg-black/40"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="overflow-hidden relative z-10 w-full max-w-4xl bg-white rounded-2xl ring-1 ring-gray-100 shadow-2xl max-h-[90vh] flex flex-col"
            role="dialog"
            aria-modal="true"
          >
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-500" />

            {/* Fixed Header */}
            <div className="flex flex-shrink-0 justify-between items-center p-6 border-b border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900">Confirm Redemption</h3>
              <button
                onClick={onClose}
                className="grid place-items-center w-10 h-10 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100"
              >
                <X size={20} />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="overflow-y-auto flex-1">
              <div className="p-6">
                <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                  {/* Left Column */}
                  <div className="space-y-6">
                    {/* Icon and Main Info */}
                    <div className="text-center">
                      <div className="flex justify-center mb-4">
                        {getIcon()}
                      </div>
                      <h4 className="mb-2 text-lg font-semibold text-gray-900">
                        Ready to redeem your points?
                      </h4>
                      <p className="text-sm text-gray-600">
                        Please confirm the details below before proceeding.
                      </p>
                    </div>

                    {/* Redemption Details */}
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <h5 className="mb-3 font-medium text-gray-900">Redemption Summary</h5>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Points to redeem:</span>
                          <span className="font-semibold text-gray-900">{redeemData.points.toLocaleString()} points</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Converting to:</span>
                          <span className="font-semibold text-gray-900">{redeemData.conversionTypeLabel}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">You will receive:</span>
                          <span className="text-lg font-semibold text-emerald-600">{redeemData.conversionValue}</span>
                        </div>
                      </div>
                    </div>

                    {/* Transaction Details */}
                    <div className="p-4 bg-blue-50 rounded-xl">
                      <h5 className="mb-3 font-medium text-blue-900">📋 Transaction Details</h5>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-blue-700">Processing Time:</span>
                          <span className="font-medium text-blue-900">Instant</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-700">Delivery Method:</span>
                          <span className="font-medium text-blue-900">SMS</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-700">Expiry:</span>
                          <span className="font-medium text-blue-900">
                            {redeemData.conversionType === "data" ? "30 days" : "Never"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-6">
                    {/* Warning Notice */}
                    <div className="flex gap-3 items-start p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="mb-1 text-sm font-medium text-amber-800">⚠️ Important Notice</p>
                        <p className="text-xs text-amber-700">
                          Once confirmed, this redemption cannot be undone. Please ensure your details are correct.
                        </p>
                      </div>
                    </div>

                    {/* Benefits Info */}
                    <div className="flex gap-3 items-start p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="mb-1 text-sm font-medium text-emerald-800">✅ What happens next?</p>
                        <p className="text-xs text-emerald-700">
                          {redeemData.conversionType === "data" 
                            ? "Your data bundle will be sent to your registered phone number within 5 minutes."
                            : "Airtime will be credited to your registered phone number within 5 minutes."
                          }
                        </p>
                      </div>
                    </div>

                    {/* Additional Info */}
                    <div className="p-4 bg-purple-50 rounded-xl">
                      <h5 className="mb-3 font-medium text-purple-900">💡 Good to Know</h5>
                      <ul className="space-y-2 text-xs text-purple-700">
                        <li>• You'll receive an SMS confirmation once processed</li>
                        <li>• {redeemData.conversionType === "data" ? "Data bundles are automatically activated" : "Airtime is instantly available for use"}</li>
                        <li>• Check your phone's balance to confirm receipt</li>
                        <li>• Contact support if you don't receive within 10 minutes</li>
                      </ul>
                    </div>

                    {/* Terms Reminder */}
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-xs text-gray-600">
                        By confirming, you agree to our{" "}
                        <button className="text-emerald-600 underline hover:text-emerald-700">
                          Terms & Conditions
                        </button>{" "}
                        and{" "}
                        <button className="text-emerald-600 underline hover:text-emerald-700">
                          Redemption Policy
                        </button>
                        .
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Fixed Actions at Bottom */}
            <div className="flex flex-shrink-0 gap-3 p-6 border-t border-gray-100">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 font-medium text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                className="flex-1 px-6 py-3 font-medium text-white bg-emerald-500 rounded-lg transition-colors hover:bg-emerald-600"
              >
                Confirm Redemption
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ConfirmRedeemModal;
